import { useEffect, useState } from 'react';

// Chrome's non-standard install-prompt event. Not in lib.dom.
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}

const DISMISS_KEY = 'kept:install-dismissed';

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

function isIos(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) && !/CriOS|FxiOS/.test(ua);
}

/**
 * Install-prompt state for the guest page. Returns:
 *  - `mode`: 'chrome' (native prompt available), 'ios' (show manual hint), or null
 *  - `promptInstall`: fires the browser dialog on Chrome/Edge/Android
 *  - `dismiss`: hides the pill for this browser
 */
export function useInstallPrompt(): {
  mode: 'chrome' | 'ios' | null;
  promptInstall: () => Promise<void>;
  dismiss: () => void;
} {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [iosVisible, setIosVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === '1') {
      setDismissed(true);
      return;
    }
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    // iOS never fires beforeinstallprompt. Show a manual hint after a short
    // delay so we don't crowd the first paint.
    if (isIos()) {
      const t = window.setTimeout(() => setIosVisible(true), 1500);
      return () => {
        window.clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onPrompt);
      };
    }
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const promptInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
    setDeferred(null);
    setIosVisible(false);
  };

  if (dismissed || isStandalone()) return { mode: null, promptInstall, dismiss };
  if (deferred) return { mode: 'chrome', promptInstall, dismiss };
  if (iosVisible) return { mode: 'ios', promptInstall, dismiss };
  return { mode: null, promptInstall, dismiss };
}
